import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, FileText, Landmark, Loader2, MapPin, MessageSquarePlus,
  ShoppingBag, User, Wallet,
} from "lucide-react";
import { api, apiError, fmt } from "@/lib/api";
import { CrmStatusPill } from "./AdminCustomers";

const INPUT = "h-10 w-full rounded-lg border border-white/10 bg-black/40 px-3 text-sm text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none";

const Section = ({ icon: Icon, title, children, testid, action }) => (
  <div className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-5" data-testid={testid}>
    <div className="mb-3 flex items-center justify-between">
      <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-neutral-400">
        <Icon className="h-4 w-4 text-amber-400" /> {title}
      </h2>
      {action}
    </div>
    {children}
  </div>
);

const AddrBlock = ({ a }) => (
  <div className="rounded-xl bg-black/40 p-3 text-xs text-neutral-400">
    <p className="font-bold text-neutral-200">{a.label || a.name || "Address"}</p>
    <p>{[a.line1 || a.address_line1, a.line2].filter(Boolean).join(", ")}</p>
    <p>{[a.city, a.state, a.pin || a.pincode].filter(Boolean).join(", ")}</p>
    {a.mobile && <p className="text-neutral-600">{a.mobile}</p>}
  </div>
);

const PAY_TYPE = {
  cod_collection: { label: "COD collection", cls: "text-amber-300" },
  credit_payment: { label: "Credit payment", cls: "text-sky-300" },
  online: { label: "Online", cls: "text-emerald-400" },
};

export default function AdminCustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/crm/customers/${id}`)
      .then(({ data }) => { setData(data); setNewStatus(data.customer.status); })
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
        else toast.error(apiError(e, "Couldn't load the customer"));
      });
  }, [id]);
  useEffect(load, [load]);

  if (notFound) return (
    <div className="py-16 text-center text-sm text-neutral-500" data-testid="cust-not-found">Customer not found.</div>
  );
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-neutral-600" /></div>;

  const { customer: c, business: b, addresses, metrics: m, credit, frequently_purchased: top,
          orders, invoices, payments, notes, audit } = data;

  const saveStatus = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/crm/customers/${id}/status`, { status: newStatus, note: statusNote });
      toast.success(`Status updated to ${newStatus}`);
      setStatusNote("");
      load();
    } catch (e) {
      toast.error(apiError(e, "Couldn't update the status"));
    } finally {
      setBusy(false);
    }
  };

  const addNote = async () => {
    if (note.trim().length < 2) { toast.error("Write a note first"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/crm/customers/${id}/notes`, { note: note.trim() });
      toast.success("Note added");
      setNote("");
      load();
    } catch (e) {
      toast.error(apiError(e, "Couldn't add the note"));
    } finally {
      setBusy(false);
    }
  };

  const kpi = (label, value, testid, accent = "text-white") => (
    <div className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-4">
      <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">{label}</p>
      <p className={`mt-1 text-xl font-bold ${accent}`} data-testid={testid}>{value}</p>
    </div>
  );

  return (
    <div data-testid="admin-customer-detail" className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to="/admin/customers" data-testid="cust-back"
            className="mb-2 inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-amber-400">
            <ArrowLeft className="h-3.5 w-3.5" /> All customers
          </Link>
          <h1 className="flex flex-wrap items-center gap-3 text-2xl font-bold text-white">
            {c.business_name || c.name} <CrmStatusPill status={c.status} testid="cust-detail-status" />
          </h1>
          <p className="mt-1 text-sm text-neutral-500">
            {c.business_name ? `${c.name} · ` : ""}{c.email}{c.phone ? ` · ${c.phone}` : ""}
            {c.created_at ? ` · customer since ${String(c.created_at).slice(0, 10)}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2 rounded-2xl border border-white/[0.07] bg-white/[0.02] p-3">
          <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} data-testid="cust-status-select" className={`${INPUT} w-32`}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="blocked">Blocked</option>
          </select>
          <input value={statusNote} onChange={(e) => setStatusNote(e.target.value)} placeholder="Reason (optional)"
            data-testid="cust-status-note" className={`${INPUT} w-44`} />
          <button onClick={saveStatus} disabled={busy || newStatus === c.status} data-testid="cust-status-save"
            className="h-10 rounded-full bg-amber-500 px-5 text-xs font-bold text-black hover:bg-amber-400 disabled:opacity-40">
            Update status
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {kpi("Total orders", m.total_orders, "cust-kpi-orders")}
        {kpi("Lifetime sales", fmt(m.lifetime_sales), "cust-kpi-lifetime")}
        {kpi("Avg order value", fmt(m.average_order_value), "cust-kpi-aov")}
        {kpi("Outstanding", fmt(m.outstanding.total), "cust-kpi-outstanding", m.outstanding.total > 0 ? "text-amber-300" : "text-emerald-400")}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section icon={User} title="Business & billing" testid="cust-business">
          {b ? (
            <div className="space-y-1.5 text-xs text-neutral-400">
              {b.business_name && <p><span className="text-neutral-600">Business:</span> <span className="text-neutral-200">{b.business_name}</span></p>}
              {b.contact_person && <p><span className="text-neutral-600">Contact:</span> <span className="text-neutral-200">{b.contact_person}</span></p>}
              {b.business_email && <p><span className="text-neutral-600">Billing email:</span> {b.business_email}</p>}
              {b.phone && <p><span className="text-neutral-600">Phone:</span> {b.phone}</p>}
              {b.gstin && <p><span className="text-neutral-600">GSTIN:</span> {b.gstin}</p>}
              {b.billing_address && <div className="pt-1"><AddrBlock a={b.billing_address} /></div>}
            </div>
          ) : (
            <p className="text-xs text-neutral-600">No business profile filled in yet.</p>
          )}
        </Section>

        <Section icon={MapPin} title={`Delivery addresses (${addresses.length})`} testid="cust-addresses">
          {addresses.length === 0 ? (
            <p className="text-xs text-neutral-600">No saved delivery addresses.</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {addresses.map((a, i) => <AddrBlock key={a.id || i} a={a} />)}
            </div>
          )}
        </Section>
      </div>

      {credit && (
        <Section icon={Landmark} title="PAQVERO Credit" testid="cust-credit-block"
          action={<Link to="/admin/credit" data-testid="cust-credit-link" className="text-xs font-bold text-amber-400 hover:text-amber-300">Open Credit →</Link>}>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {kpi("Credit limit", fmt(credit.limit), "cust-credit-limit")}
            {kpi("Used", fmt(credit.used), "cust-credit-used", credit.used > 0 ? "text-amber-300" : "text-white")}
            {kpi("Available", fmt(credit.available), "cust-credit-available", "text-emerald-400")}
            {kpi("Status", credit.status, "cust-credit-status", credit.status === "APPROVED" ? "text-emerald-400" : "text-red-400")}
          </div>
          {credit.overdue > 0 && (
            <p className="mt-2 text-xs font-bold text-red-400" data-testid="cust-credit-overdue">
              Overdue: {fmt(credit.overdue)}{credit.next_due_date ? ` · oldest due ${String(credit.next_due_date).slice(0, 10)}` : ""}
            </p>
          )}
          <p className="mt-2 text-[11px] text-neutral-600">Receivables split: unpaid orders {fmt(m.outstanding.unpaid_orders)} + credit outstanding {fmt(m.outstanding.credit_outstanding)}</p>
        </Section>
      )}

      <Section icon={ShoppingBag} title="Frequently purchased" testid="cust-top-products">
        {top.length === 0 ? (
          <p className="text-xs text-neutral-600">No purchases yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[480px] text-left text-xs">
              <thead>
                <tr className="border-b border-white/[0.07] text-[10px] uppercase tracking-wider text-neutral-500">
                  <th className="py-2 pr-3">Product</th><th className="py-2 pr-3">SKU</th>
                  <th className="py-2 pr-3 text-right">Units</th><th className="py-2 pr-3 text-right">Orders</th>
                  <th className="py-2 text-right">Spend</th>
                </tr>
              </thead>
              <tbody>
                {top.map((t) => (
                  <tr key={t.sku} className="border-b border-white/[0.05]">
                    <td className="max-w-[220px] truncate py-2 pr-3 text-neutral-300">{t.name}</td>
                    <td className="py-2 pr-3 font-mono text-amber-400">{t.sku}</td>
                    <td className="py-2 pr-3 text-right font-bold text-white">{t.qty}</td>
                    <td className="py-2 pr-3 text-right text-neutral-400">{t.orders}</td>
                    <td className="py-2 text-right text-neutral-300">{fmt(t.spend)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section icon={ShoppingBag} title={`Recent orders (${m.total_orders})`} testid="cust-orders"
          action={<Link to="/admin/orders" data-testid="cust-orders-link" className="text-xs font-bold text-amber-400 hover:text-amber-300">Open Orders →</Link>}>
          {orders.length === 0 ? <p className="text-xs text-neutral-600">No orders yet.</p> : (
            <div className="space-y-1.5">
              {orders.map((o) => (
                <div key={o.id} className="flex items-center justify-between gap-2 text-xs" data-testid={`cust-order-${o.id}`}>
                  <span className="font-mono text-amber-400">{o.order_number}</span>
                  <span className="text-neutral-500">{String(o.created_at).slice(0, 10)} · {o.item_count} item(s)</span>
                  <span className="text-neutral-300">{fmt(o.total)}</span>
                  <span className={`font-bold ${o.status === "cancelled" ? "text-red-400" : o.payment?.status === "paid" ? "text-emerald-400" : "text-amber-300"}`}>
                    {o.status === "cancelled" ? "cancelled" : (o.payment?.status || "").replace(/_/g, " ")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section icon={FileText} title={`Invoices (${invoices.length})`} testid="cust-invoices"
          action={<Link to="/admin/invoices" data-testid="cust-invoices-link" className="text-xs font-bold text-amber-400 hover:text-amber-300">Open Invoices →</Link>}>
          {invoices.length === 0 ? <p className="text-xs text-neutral-600">No invoices yet.</p> : (
            <div className="space-y-1.5">
              {invoices.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between gap-2 text-xs" data-testid={`cust-invoice-${inv.id}`}>
                  <span className="font-mono text-amber-400">{inv.invoice_number}</span>
                  {inv.proforma && <span className="rounded-full border border-amber-500/30 px-2 py-0.5 text-[9px] font-bold uppercase text-amber-300">proforma</span>}
                  <span className="text-neutral-500">{String(inv.invoice_date).slice(0, 10)}</span>
                  <span className="text-neutral-300">{fmt(inv.grand_total)}</span>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      <Section icon={Wallet} title="Payment history" testid="cust-payments">
        {payments.length === 0 ? <p className="text-xs text-neutral-600">No payments recorded yet.</p> : (
          <div className="space-y-1.5">
            {payments.map((p, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`cust-payment-${i}`}>
                <span className="text-neutral-500">{p.date}</span>
                <span className={`font-bold ${PAY_TYPE[p.type]?.cls || "text-neutral-300"}`}>{PAY_TYPE[p.type]?.label || p.type}</span>
                <span className="font-bold text-white">{fmt(p.amount)}</span>
                <span className="text-neutral-500">{p.method.replace(/_/g, " ")}{p.reference ? ` · ${p.reference}` : ""}</span>
                {p.order_number && <span className="font-mono text-neutral-500">{p.order_number}</span>}
                {p.by && <span className="text-neutral-600">by {p.by}</span>}
              </div>
            ))}
          </div>
        )}
      </Section>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section icon={MessageSquarePlus} title={`Internal notes (${notes.length})`} testid="cust-notes">
          <div className="mb-3 flex gap-2">
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note…"
              data-testid="cust-note-input" className={INPUT} />
            <button onClick={addNote} disabled={busy} data-testid="cust-note-add"
              className="h-10 shrink-0 rounded-full bg-amber-500 px-5 text-xs font-bold text-black hover:bg-amber-400 disabled:opacity-40">
              Add
            </button>
          </div>
          {notes.length === 0 ? <p className="text-xs text-neutral-600">No notes yet — only admins can see these.</p> : (
            <div className="space-y-2">
              {[...notes].reverse().map((n) => (
                <div key={n.id} className="rounded-xl bg-black/40 p-3" data-testid={`cust-note-${n.id}`}>
                  <p className="text-xs text-neutral-200">{n.note}</p>
                  <p className="mt-1 text-[10px] text-neutral-600">{n.author} · {String(n.at).slice(0, 16).replace("T", " ")}</p>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section icon={FileText} title="CRM audit history" testid="cust-audit">
          {audit.length === 0 ? <p className="text-xs text-neutral-600">No CRM changes yet.</p> : (
            <div className="space-y-1.5">
              {[...audit].reverse().map((h, i) => (
                <p key={i} className="text-xs text-neutral-400" data-testid={`cust-audit-${i}`}>
                  <span className="text-neutral-600">{String(h.at).slice(0, 16).replace("T", " ")}</span> ·{" "}
                  <strong className="text-neutral-300">{h.action.replace(/_/g, " ")}</strong> · {h.actor}
                  {h.details ? ` — ${h.details}` : ""}
                </p>
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}
